import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { ChevronDown, FileText, Trash2, X } from 'lucide-react';
import { isSenderSignature, type SignatureAnchor, type SignatureDocument, type SignatureStatus } from '@worki/shared';
import { api, authedFetch, RequestError } from '../../lib/api.js';
import { signAsSender } from '../../lib/anchors.js';
import { savedSignatures } from '../../lib/savedSignature.js';
import { useSession } from '../../lib/session.js';
import { AnchoredMenu } from '../../components/AnchoredMenu.js';
import { Button, ErrorNotice, IconButton } from '../../components/ui.js';
import { ConfirmDialog } from '../../components/Modal.js';
import { Icon } from '../../components/Icon.js';
import { deviceStore } from '../../components/PlaceDialogs.js';
import { SignaturePlacer } from '../../components/SignaturePlacer.js';
import { useToast } from '../../components/Toast.js';

/**
 * SIG-004 — the pieces both signing screens are built from.
 *
 * The list and the composing screen show the same document in two moments of
 * its life, and the parts that say what a document *is* — its status, who it
 * went to, its pages with the boxes on them — should not drift apart between
 * them.
 */

const TONES: Record<SignatureStatus, string> = {
  draft: 'neutral',
  sent: 'warning',
  viewed: 'warning',
  signed: 'success',
  declined: 'danger',
  expired: 'neutral',
  cancelled: 'danger',
};

export function StatusBadge({ status }: { status: SignatureStatus }) {
  const { t } = useTranslation();
  return <span className={`badge badge--${TONES[status]}`}>{t(`signing.status.${status}`)}</span>;
}

/**
 * The number a document is referred to by, padded the way a quote's is.
 *
 * Documents written before numbering have none; they print a dash rather than
 * a zero, which would read as a real document number nobody could find.
 */
export function documentNumber(row: SignatureDocument): string {
  return row.number > 0 ? String(row.number).padStart(2, '0') : '—';
}

/** The one line that says who this document is about, wherever it is listed. */
export function recipientLine(row: SignatureDocument): string {
  return [row.recipientName, row.recipientPhone ?? row.recipientEmail].filter(Boolean).join(' · ');
}

/** The file name without its extension, as the document's default title. */
export const titleFromFile = (name: string): string => name.replace(/\.[^.]+$/, '').slice(0, 160);

export const FILE_ACCEPT =
  'application/pdf,.pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

export const isWordFile = (file: File): boolean =>
  file.name.toLowerCase().endsWith('.docx') ||
  file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/**
 * A multipart request, by hand.
 *
 * `fetch` rather than the shared client for the same reason the old screen used
 * it: the boundary has to be the browser's, so nothing sets a content type here.
 */
export async function postFile<T>(path: string, body: FormData): Promise<T> {
  const response = await authedFetch(path, { method: 'POST', body });
  const payload: unknown = response.headers.get('content-type')?.includes('application/json')
    ? await response.json()
    : null;

  if (!response.ok) {
    const error = (payload as { error?: { code?: string; fields?: Record<string, string> } } | null)?.error;
    // The field message is the useful one — "only PDF or Word" rather than
    // "validation failed" — so it is what surfaces when there is one.
    const fields = error?.fields;
    throw new RequestError(response.status, fields?.file ?? Object.values(fields ?? {})[0] ?? error?.code ?? 'errors.unknown');
  }
  return payload as T;
}

/** Replacing the file on a document that already exists. */
export async function uploadSource(id: string, file: File, expectedRevision?: number): Promise<SignatureDocument> {
  const body = new FormData();
  body.append('file', file);
  if (expectedRevision !== undefined) body.append('expectedRevision', String(expectedRevision));
  return postFile<SignatureDocument>(`/signatures/${id}/source`, body);
}

/**
 * The document, and where the signature goes on it.
 *
 * What is drawn is the caller's to choose, which is the whole reason this
 * screen could be split in two: on an existing document it is the server's
 * copy, addressed by URL, and on a document being composed it is the picked
 * file itself, still on this machine and never uploaded.
 */
export function DocumentPages({
  src,
  anchors,
  onAnchorsChange,
  onPages,
  refreshKey,
  label,
  meta,
  signerCount,
  signerNames,
  onSignerNameChange,
  tall,
  boxClassName,
}: {
  src: string | Blob;
  anchors: SignatureAnchor[];
  onAnchorsChange?: (anchors: SignatureAnchor[]) => void;
  onPages?: (count: number) => void;
  refreshKey: string;
  label: string;
  meta?: string;
  /** SIG-003 — how many people sign, and what to call them. */
  signerCount?: number;
  signerNames?: Record<number, string>;
  onSignerNameChange?: (position:number,name:string)=>void;
  /** 1.31 — the taller box, for the screens where the sender marks the pages. */
  tall?: boolean;
  /**
   * Extra classes for the box the pages scroll in.
   *
   * The default box caps its own height and keeps the scroll to itself, which
   * is right on the compose screen: the page around it scrolls, and a wheel
   * over the document should move the document. Inside a panel the same rule is
   * a trap — the panel is the only thing that scrolls, so reaching the last
   * page stopped the wheel dead with the send controls still below the fold.
   */
  boxClassName?: string;
}) {
  const [placement, setPlacement] = useState<{ image: string; src: string | Blob; key: string } | null>(null);
  const editable = Boolean(onAnchorsChange);
  useEffect(() => setPlacement(null), [src, refreshKey, editable]);
  const savedPlacement = onAnchorsChange && placement?.src === src && placement.key === refreshKey ? placement.image : null;
  return (
    <div className="stack">
      <div className="row row--wrap">
        {!editable && <span className="muted">{label}</span>}
        {meta && <span className="list-row__meta ltr-nums">{meta}</span>}
      </div>

      <SignaturePlacer
        key={refreshKey}
        src={src}
        anchors={anchors}
        savedPlacement={savedPlacement}
        onPlacementEnd={() => setPlacement(null)}
        {...(onAnchorsChange ? { onChange: onAnchorsChange } : {})}
        {...(onPages ? { onPages } : {})}
        {...(signerCount ? { signerCount } : {})}
        {...(signerNames ? { signerNames } : {})}
        {...(onSignerNameChange ? {onSignerNameChange} : {})}
        {...(tall ? { tall } : {})}
        {...(boxClassName ? { className: boxClassName } : {})}
      />

      {onAnchorsChange && <PlacesLine anchors={anchors} onAnchorsChange={onAnchorsChange} onPlaceImage={(image) => setPlacement({ image, src, key: refreshKey })} />}
    </div>
  );
}

/**
 * 1.47 — the line under the pages: the trash and the count of areas at its
 * start, and "חתימות שמורות ▾" at its end, the left in Hebrew.
 *
 * The dropdown lists the signatures this device keeps for whoever is signed in
 * (1.28), as thumbnails. One picked goes on every place marked for the sender's
 * signature, the way a signature saved in the dialog does; with no such place
 * the next document tap places it in a new sender signature area.
 *
 * The line reads the list each time it is drawn, and it is the line that holds
 * whether the menu is open, so a signature kept in the dialog a moment ago is in
 * it when it opens, without a reload. Nothing is drawn for a person with none
 * kept, and the line is not drawn at all when there is no place to clear either.
 */
function PlacesLine({
  anchors,
  onAnchorsChange,
  onPlaceImage,
}: {
  anchors: SignatureAnchor[];
  onAnchorsChange: (anchors: SignatureAnchor[]) => void;
  onPlaceImage: (image: string) => void;
}) {
  const { t } = useTranslation();
  const owner = useSession().session?.user.id;
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const saved = owner ? savedSignatures(deviceStore(), owner) : [];

  if (anchors.length === 0 && saved.length === 0) return null;

  const place = (image: string) => {
    setOpen(false);
    if (!anchors.some((anchor) => isSenderSignature(anchor) && !anchor.annotation)) {
      onPlaceImage(image);
      return;
    }
    onAnchorsChange(signAsSender(anchors, image));
  };

  return (
    <div className="row row--wrap">
      {anchors.length > 0 && (
        <>
          <IconButton type="button" label={t('signing.placeClear')} onClick={() => onAnchorsChange([])}>
            <Trash2 className="h-4 w-4" />
          </IconButton>
          <span className="muted">{t('signing.placeCount', { count: anchors.length })}</span>
        </>
      )}
      {saved.length > 0 && (
        <>
          <button
            ref={trigger}
            type="button"
            aria-haspopup="true"
            aria-expanded={open}
            onClick={() => setOpen((was) => !was)}
            className="ms-auto h-9 px-2.5 rounded-lg border border-border bg-card text-foreground text-sm font-medium inline-flex items-center gap-1 transition-colors hover:bg-muted"
          >
            {t('signing.senderSignatureSavedList')}
            <ChevronDown className="h-3.5 w-3.5" aria-hidden />
          </button>
          <AnchoredMenu open={open} onClose={() => setOpen(false)} anchorRef={trigger} align="end" width={264}>
            <ul className="p-2 space-y-1.5 max-h-[50vh] overflow-y-auto">
              {saved.map((image) => (
                <li key={image}>
                  <button
                    type="button"
                    aria-label={t('signing.senderSignatureUseKept')}
                    title={t('signing.senderSignatureUseKept')}
                    onClick={(event) => { event.stopPropagation(); place(image); }}
                    className="block w-full rounded-lg border border-border bg-white p-1 transition-colors hover:border-primary"
                  >
                    <img src={image} alt="" className="block h-12 w-full object-contain" />
                  </button>
                </li>
              ))}
            </ul>
          </AnchoredMenu>
        </>
      )}
    </div>
  );
}

/**
 * 1.30 — the files a document to sign is made of, a line to each, above its pages.
 *
 * The card used to name the first of them and count the rest, so there was no
 * way to take one out short of replacing them all. Each line ends in its own X,
 * which is the top-left corner of it in Hebrew, and pressing it is the
 * caller's to answer: the rest have to be put together again, and what was
 * marked on the old pages goes.
 *
 * `busy` while they are being put together: a second merge started over the
 * first is the one that would finish last, and with it a document of pages
 * nobody chose.
 */
export function PickedFiles({
  files,
  busy,
  onRemove,
}: {
  files: File[];
  busy: boolean;
  onRemove: (index: number) => void;
}) {
  const { t } = useTranslation();

  return (
    <ul className="space-y-1">
      {files.map((one, index) => (
        // By place, not by name: the same file can be picked twice.
        <li key={index} className="flex items-center gap-2">
          <FileText className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
          <span className="min-w-0 flex-1 truncate text-sm font-medium">{one.name}</span>
          <IconButton
            type="button"
            label={t('signing.fileRemove')}
            disabled={busy}
            onClick={() => onRemove(index)}
            className="disabled:opacity-40"
          >
            <X className="h-4 w-4" />
          </IconButton>
        </li>
      ))}
    </ul>
  );
}

/**
 * Swapping the file on a draft that already exists.
 *
 * Confirmed first, because the placements go with it. They were points on the
 * old document's pages, and carrying them over would leave a signature box
 * floating mid-paragraph in the new one — wrong in a way nobody notices until
 * the signed copy comes back.
 */
export function ReplaceFile({
  document,
  onReplaced,
}: {
  document: SignatureDocument;
  onReplaced: (document: SignatureDocument) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState<File | null>(null);

  const replace = useMutation({
    mutationFn: (file: File) => uploadSource(document.id, file, document.contentRevision),
    onSuccess: (updated) => {
      setPending(null);
      onReplaced(updated);
    },
    onError: (error) => {
      setPending(null);
      toast.error(t((error as RequestError).code === 'errors.conflict' ? 'signing.revisionChanged' : (error as RequestError).code));
    },
  });

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={FILE_ACCEPT}
        className="hidden"
        onChange={(event) => {
          const picked = event.target.files?.[0];
          if (picked) setPending(picked);
          event.target.value = '';
        }}
      />
      <Button
        variant="ghost"
        icon={<Icon name="upload" size={17} />}
        loading={replace.isPending}
        onClick={() => inputRef.current?.click()}
      >
        {replace.isPending ? t('signing.fileUploading') : t('signing.fileReplace')}
      </Button>

      {replace.isError && <>
        <ErrorNotice code={(replace.error as RequestError).code === 'errors.conflict' ? 'signing.revisionChanged' : (replace.error as RequestError).code} />
        {(replace.error as RequestError).code === 'errors.conflict' && <Button onClick={() => void api.get<SignatureDocument>(`/signatures/${document.id}`).then(next => { onReplaced(next); replace.reset(); })}>{t('publicSign.reloadLatest')}</Button>}
      </>}

      <ConfirmDialog
        open={pending !== null}
        title={t('signing.fileReplace')}
        message={t('signing.fileReplaceWarning')}
        confirmLabel={t('signing.fileReplace')}
        loading={replace.isPending}
        onConfirm={() => pending && replace.mutate(pending)}
        onCancel={() => setPending(null)}
      />
    </>
  );
}
